import React, { useEffect, useState } from 'react'
import { db } from '../../../firebase'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import Icon from '../../../components/AppIcon'
import Button from '../../../components/ui/Button'
import { logGoldEvent } from '../lib/goldAudit'

const FIELDS = [
  {
    key: 'desagioPercent',
    label: 'Deságio — Jewelry (%)',
    hint: 'Applies to: ring, chain, bracelet, anklet, necklace, pendant, earring, watch, scrap and other. Current production value: 30%.',
    min: 0, max: 100, step: 0.5,
  },
  {
    key: 'desagioCoinBarPercent',
    label: 'Deságio — Coin / Bar (%)',
    hint: 'Applies to: coin and bar only. Current production value: 20%.',
    min: 0, max: 100, step: 0.5,
  },
  { key: 'priceLockMinutes', label: 'Price lock (minutes)', hint: 'How long a quote stays valid', min: 1, max: 120, step: 1 },
]

export default function ExchangeSettings() {
  const [form, setForm] = useState(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const snap = await getDoc(doc(db, 'gold_settings', 'config'))
        const d = snap.exists() ? snap.data() : {}
        if (alive) setForm({
          desagioPercent: d.desagioPercent ?? 30,
          desagioCoinBarPercent: d.desagioCoinBarPercent ?? 20,
          priceLockMinutes: d.priceLockMinutes ?? 30,
          currency: d.currency ?? 'USD',
        })
      } catch (e) {
        if (alive) setError(e?.message || 'Failed to load settings')
      }
    })()
    return () => { alive = false }
  }, [])

  const update = (key, value) => { setForm((f) => ({ ...f, [key]: value })); setSaved(false) }

  const save = async () => {
    setSaving(true); setError(''); setSaved(false)
    try {
      await setDoc(doc(db, 'gold_settings', 'config'), {
        desagioPercent: Number(form.desagioPercent),
        desagioCoinBarPercent: Number(form.desagioCoinBarPercent),
        priceLockMinutes: Number(form.priceLockMinutes),
        currency: String(form.currency || 'USD').toUpperCase().slice(0, 3),
        updatedAt: serverTimestamp(),
      }, { merge: true })
      logGoldEvent('settings_updated', {
        desagioPercent: Number(form.desagioPercent),
        desagioCoinBarPercent: Number(form.desagioCoinBarPercent),
        priceLockMinutes: Number(form.priceLockMinutes),
      })
      setSaved(true)
    } catch (e) {
      setError(e?.message || 'Failed to save (admin only)')
    } finally {
      setSaving(false)
    }
  }

  if (error && !form) {
    return <div className="bg-card rounded-2xl border border-border p-6 text-sm text-destructive">{error}</div>
  }
  if (!form) {
    return <div className="bg-card rounded-2xl border border-border p-8 text-center text-muted-foreground text-sm">Loading settings…</div>
  }

  return (
    <div className="bg-card rounded-2xl border border-border p-5 sm:p-6 max-w-xl">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
          <Icon name="Settings" size={22} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Settings</h2>
          <p className="text-sm text-muted-foreground">Deságio, price lock and currency for the Exchange Desk</p>
        </div>
      </div>

      <div className="space-y-4">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-medium text-foreground mb-1">{f.label}</label>
            <input
              type="number"
              min={f.min} max={f.max} step={f.step}
              value={form[f.key]}
              onChange={(e) => update(f.key, e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-border bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <p className="text-xs text-muted-foreground mt-1">{f.hint}</p>
          </div>
        ))}

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Currency</label>
          <input
            type="text"
            maxLength={3}
            value={form.currency}
            onChange={(e) => update('currency', e.target.value.toUpperCase())}
            className="w-32 h-10 px-3 rounded-lg border border-border bg-input text-foreground uppercase focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </div>

      {error && <p className="text-sm text-destructive mt-4">{error}</p>}

      <div className="flex items-center gap-3 mt-6">
        <Button variant="default" onClick={save} loading={saving} iconName="Check" iconPosition="left">
          Save settings
        </Button>
        {saved && <span className="text-sm text-success flex items-center gap-1"><Icon name="CheckCircle" size={16} /> Saved</span>}
      </div>
    </div>
  )
}
